import React, { useState, useEffect, useRef } from "react";
import Modal from "../Modal/Modal";
import "./ViewImagesModal.scss";

import JobService from "../../services/JobService";
import ContainerType from "../ContainerType/ContainerType";
import ContainerStatus from "../ContainerStatus/ContainerStatus";
import { containerSizeStringToNumber } from "../../utils/general";
import { JobBreakUpDetail } from "../../interfaces/job.interface";

interface ViewImagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  tripId: number | null;
  position?: string;
  title?: string;
  tripData?: Record<string, any> | null; // Full trip object
}

const ViewImagesModal: React.FC<ViewImagesModalProps> = ({
  isOpen,
  onClose,
  tripId,
  position,
  title = "Trip Images & Details",
  tripData = null,
}) => {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [images, setImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showZoom, setShowZoom] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [lensSize, setLensSize] = useState({ width: 32, height: 32 });
  const imageRef = useRef<HTMLImageElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation for accessibility
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "ArrowLeft") setSelectedIdx((idx) => Math.max(0, idx - 1));
    if (e.key === "ArrowRight")
      setSelectedIdx((idx) => Math.min(images.length - 1, idx + 1));
  };

  // Handle mouse move for zoom effect
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current || !zoomRef.current) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    const zoomRect = zoomRef.current.getBoundingClientRect();

    const mouseX = ((e.clientX - imageRect.left) / imageRect.width) * 100;
    const mouseY = ((e.clientY - imageRect.top) / imageRect.height) * 100;

    // Calculate lens size based on zoom container aspect ratio and zoom level
    const zoomLevel = 2.5; // 250% zoom

    // Calculate what percentage of the image should be visible in the lens
    // to fill the zoom container when magnified
    const imageAspectRatio = imageRect.width / imageRect.height;
    const zoomAspectRatio = zoomRect.width / zoomRect.height;

    let lensWidth: number;
    let lensHeight: number;

    if (imageAspectRatio > zoomAspectRatio) {
      // Image is wider than zoom container
      lensHeight = (100 / zoomLevel) * 0.8; // Make it smaller to fit better
      lensWidth = (lensHeight * zoomAspectRatio) / imageAspectRatio;
    } else {
      // Image is taller than zoom container
      lensWidth = (100 / zoomLevel) * 0.8; // Make it smaller to fit better
      lensHeight = (lensWidth * imageAspectRatio) / zoomAspectRatio;
    }

    // Update lens size state
    setLensSize({ width: lensWidth, height: lensHeight });

    const lensX = Math.max(
      0,
      Math.min(100 - lensWidth, mouseX - lensWidth / 2)
    );
    const lensY = Math.max(
      0,
      Math.min(100 - lensHeight, mouseY - lensHeight / 2)
    );

    // Zoom position should be the center of the lens
    const zoomX = lensX + lensWidth / 2;
    const zoomY = lensY + lensHeight / 2;

    setZoomPosition({ x: zoomX, y: zoomY });
    setLensPosition({ x: lensX, y: lensY });
  };

  const handleMouseEnter = () => {
    setShowZoom(true);
  };

  const handleMouseLeave = () => {
    setShowZoom(false);
  };

  useEffect(() => {
    setSelectedIdx(0);
    if (isOpen && tripId) {
      setLoading(true);
      setError(null);
      // JobService.viewImages now uses GET with query params (tripId, position)
      JobService.viewImages({ tripId, position })
        .then((res) => {
          if (res.data && Array.isArray(res.data.data)) {
            setImages(res.data.data.filter((url: string) => !!url));
          } else {
            setImages([]);
          }
        })
        .catch((err) => {
          setError("Failed to load images");
          setImages([]);
        })
        .finally(() => setLoading(false));
    } else {
      setImages([]);
    }
  }, [isOpen, tripId, position]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="xl"
      closeOnModalOverlayClick={true}
    >
      <div className="view-images-modal-container">
        {loading ? (
          <div className="text-gray-500 p-8 text-lg">Loading images...</div>
        ) : error ? (
          <div className="text-red-500 p-8 text-lg">{error}</div>
        ) : images.length > 0 ? (
          <>
            {/* Main image viewer with zoom functionality */}
            <div className="view-images-content">
              {/* Left side - Main image with hover zoom */}
              <div
                className="view-images-main"
                tabIndex={0}
                onKeyDown={handleKeyDown}
                aria-label="Image viewer"
              >
                {/* Thumbnails - Vertical on left */}
                <div className="view-images-thumbnails-vertical">
                  {images.map((url, idx) => (
                    <img
                      key={idx}
                      src={url}
                      alt={`Thumbnail ${idx + 1}`}
                      className={selectedIdx === idx ? "selected" : ""}
                      onClick={() => setSelectedIdx(idx)}
                    />
                  ))}
                </div>

                {/* Main image container */}
                <div className="main-image-wrapper">
                  <div
                    className="image-container"
                    onMouseMove={handleMouseMove}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    <img
                      ref={imageRef}
                      src={images[selectedIdx]}
                      alt={`Trip ${selectedIdx + 1}`}
                      className="main-image"
                    />
                    {/* Zoom lens overlay */}
                    {showZoom && (
                      <div
                        className="zoom-lens"
                        style={{
                          left: `${lensPosition.x}%`,
                          top: `${lensPosition.y}%`,
                          width: `${lensSize.width}%`,
                          height: `${lensSize.height}%`,
                        }}
                      />
                    )}
                  </div>
                  <div className="image-label">
                    {`Image ${selectedIdx + 1} of ${images.length}`}
                  </div>
                </div>
              </div>

              {/* Right side - Trip Details */}
              <div className="view-images-details">
                <h3 className="details-title">Trip Details</h3>

                {tripData ? (
                  <div className="details-content">
                    {tripData.containerNumber && (
                      <div className="detail-row">
                        <span className="detail-label">Container Number:</span>
                        <span className="detail-value">
                          {tripData.containerNumber}
                        </span>
                      </div>
                    )}
                    {(tripData.containerSize ||
                      tripData.containerType ||
                      tripData.containerSiTy) && (
                      <div className="detail-row">
                        <span className="detail-label">Size & Type:</span>
                        <span className="detail-value">
                          {tripData.containerSize && tripData.containerType
                            ? (() => {
                                const size = containerSizeStringToNumber(
                                  tripData.containerSize
                                );
                                return size !== null ? (
                                  <ContainerType
                                    size={size}
                                    type={tripData.containerType}
                                  />
                                ) : (
                                  tripData.containerSiTy
                                );
                              })()
                            : tripData.containerSiTy}
                        </span>
                      </div>
                    )}
                    {tripData.jobBreakupDetailId && (
                      <div className="detail-row">
                        <span className="detail-label">Trip ID:</span>
                        <span className="detail-value">
                          {tripData.jobBreakupDetailId}
                        </span>
                      </div>
                    )}
                    {tripData.jobCode && (
                      <div className="detail-row">
                        <span className="detail-label">Job Code:</span>
                        <span className="detail-value">{tripData.jobCode}</span>
                      </div>
                    )}
                    {tripData.truckIdName && tripData.truckIdName !== "N/A" && (
                      <div className="detail-row">
                        <span className="detail-label">Truck Number:</span>
                        <span className="detail-value">
                          {tripData.truckIdName}
                        </span>
                      </div>
                    )}
                    {tripData.driverIdName &&
                      tripData.driverIdName !== "N/A" && (
                        <div className="detail-row">
                          <span className="detail-label">Driver Name:</span>
                          <span className="detail-value">
                            {tripData.driverIdName}
                          </span>
                        </div>
                      )}
                    {tripData.transporterIdName &&
                      tripData.transporterIdName !== "N/A" && (
                        <div className="detail-row">
                          <span className="detail-label">Transporter:</span>
                          <span className="detail-value">
                            {tripData.transporterIdName}
                          </span>
                        </div>
                      )}
                    {tripData.source && (
                      <div className="detail-row">
                        <span className="detail-label">Source:</span>
                        <span className="detail-value">{tripData.source}</span>
                      </div>
                    )}
                    {tripData.destination && (
                      <div className="detail-row">
                        <span className="detail-label">Destination:</span>
                        <span className="detail-value">
                          {tripData.destination}
                        </span>
                      </div>
                    )}
                    {tripData.pickedAt && tripData.pickedAt !== "N/A" && (
                      <div className="detail-row">
                        <span className="detail-label">Picked At:</span>
                        <span className="detail-value">
                          {tripData.pickedAt}
                        </span>
                      </div>
                    )}
                    {tripData.droppedAt && tripData.droppedAt !== "N/A" && (
                      <div className="detail-row">
                        <span className="detail-label">Dropped At:</span>
                        <span className="detail-value">
                          {tripData.droppedAt}
                        </span>
                      </div>
                    )}
                    {tripData.tripStatus && (
                      <div className="detail-row">
                        <span className="detail-label">Status:</span>
                        <span className="detail-value">
                          <ContainerStatus
                            container={tripData as JobBreakUpDetail}
                          />
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-details">No trip details available</div>
                )}

                {/* Zoomed version - overlays trip details when hovering */}
                {showZoom && (
                  <div className="view-images-zoom-overlay" ref={zoomRef}>
                    <div
                      className="zoomed-image"
                      style={{
                        backgroundImage: `url(${images[selectedIdx]})`,
                        backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                        backgroundSize: "250%",
                      }}
                    />
                    {/* <div className="zoom-label">Zoomed View</div> */}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="text-gray-500">No images available.</div>
        )}
      </div>
    </Modal>
  );
};

export default ViewImagesModal;
